import { ThreadSection } from "@/components/thread-section";
import ReportDocCell from "@/components/prds/report-doc-cell";
import { useApps } from "@/hooks/apps";
import { engagementAppIds } from "@/lib/engagement";
import type { Prd, PrdThreadPost } from "@/types/domain";
import { CopyShortId } from "@/components/prds/copy-short-id";

// PRD thread: a chronological stream of markdown documents exchanged between
// each side's agent — tech posts a codebase-grounded breakdown, PM posts their
// agent's understanding back, and so on. Injected as context when
// plan/breakdown tasks are claimed. Titles (from the titled era) are not
// rendered — content only.
export const PrdThreadSection = ({
  prd,
  appId,
}: {
  prd: Prd;
  /** App context resolved from the engagement URL → tag for new posts. */
  appId?: string;
}) => {
  // Posts may be tagged with one of the PRD's apps (PRD dab8eb54); the
  // picker only makes sense with ≥2 relations — a single-app PRD has nothing
  // to tell apart.
  const allApps = useApps();
  const relIds = new Set(engagementAppIds(prd));
  const apps = allApps.filter((a) => relIds.has(String(a.id)));
  return (
    <ThreadSection<PrdThreadPost>
      endpoint={`/prds/${encodeURIComponent(prd.id)}/threads`}
      createBody={(content, postAppId) => ({
        content,
        ...(postAppId ? { appId: postAppId } : {}),
      })}
      // Work-item path (prd.appId set): posts from every app on the document
      // are listed with their tag; new posts are pinned to the path's app.
      apps={apps.length > 1 ? apps : []}
      defaultAppId={appId}
      canPickApp={!prd.appId}
      // Reports live inside the thread stream as leading items (bugs carry no
      // reports — same rule as the retired standalone Reports section).
      prepend={
        prd.type !== "bug" && prd.type !== "todo" ? (
          <ReportDocCell prd={prd} />
        ) : undefined
      }
      renderAuthor={(post) => (
        <>
          <span className="min-w-0 truncate text-sm font-medium">
            {post.author?.name ?? post.authorName ?? "unknown"}
          </span>
          {post.agentLabel && (
            <span className="shrink-0 rounded border px-1.5 py-0.5 text-xs text-muted-foreground">
              {post.agentLabel}
            </span>
          )}
          {/* PRD short ID on every post (PRD 83f31f0c): a post quoted or
              linked on its own still says which PRD it belongs to */}
          <CopyShortId shortId={prd.shortId} />
        </>
      )}
      replyPlaceholder="Reply (markdown) — e.g. paste your agent's breakdown or understanding of this PRD"
    />
  );
};
